import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../lib/auth";
import { useIntl } from "../../lib/i18n";
import { api, apiJSON } from "../../lib/api";

type PmsConfig = {
  id: string;
  kind: string;
  state: string;
  redirectUri?: string;
  providerAccountId?: string;
  connectedAt?: string;
  lastSyncAt?: string;
  lastError?: string;
  autoCreateMatter?: boolean;
};

type FieldMap = {
  kind: string;
  mapping: Record<string, unknown>;
};

// FilevineConnectForm posts {configId, clientId, clientSecret} to
// /api/v1/tenant/pms/filevine. On success the parent reloads the PMS
// config list; on failure the backend returns a descriptive message
// (filevine_verify_failed: ...) which the form surfaces inline.
function FilevineConnectForm({
  configId,
  onConnected,
}: {
  configId: string;
  onConnected: () => void;
}) {
  const { session } = useAuth();
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await api("/api/v1/tenant/pms/filevine", {
        method: "POST",
        token: session?.token,
        body: JSON.stringify({ configId, clientId, clientSecret }),
      });
      if (!res.ok) {
        const body = await res.text().catch(() => res.statusText);
        throw new Error(body || res.statusText);
      }
      onConnected();
    } catch (err) {
      setError(String((err as Error).message ?? err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="mt-3 space-y-2 rounded-md bg-slate-50 p-3" onSubmit={submit}>
      <p className="text-xs text-slate-500">
        Filevine key pair (client_credentials grant — no OAuth redirect).
      </p>
      <input
        type="text"
        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        placeholder="Client ID"
        value={clientId}
        onChange={(e) => setClientId(e.target.value)}
        required
      />
      <input
        type="password"
        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
        placeholder="Client Secret"
        value={clientSecret}
        onChange={(e) => setClientSecret(e.target.value)}
        required
      />
      {error && <p className="text-xs text-rose-600">{error}</p>}
      <button
        type="submit"
        disabled={submitting}
        className="rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-500 disabled:opacity-50"
      >
        {submitting ? "…" : "Connect"}
      </button>
    </form>
  );
}

// IntegrationDetail expands inline below the selected PMS row and shows
// the audit-relevant configuration: when the integration connected,
// when the last sync happened, the provider's account id, and the
// per-tenant Section→Matter field-map mapping. The Equipo/Admin
// screenshot of this panel doubles as the "click-into-detail" view the
// runbook references. The mapping is fetched lazily — opening the
// panel is what triggers GET /tenant/pms/{kind}/field-map.
function IntegrationDetail({ cfg }: { cfg: PmsConfig }) {
  const { session } = useAuth();
  const [fieldMap, setFieldMap] = useState<FieldMap | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    apiJSON<FieldMap>(`/api/v1/tenant/pms/${cfg.kind}/field-map`, { token: session.token })
      .then(setFieldMap)
      .catch((e) => setError(String(e?.message ?? "fetch failed")));
  }, [session, cfg.kind]);

  const mappingEntries = fieldMap?.mapping
    ? Object.entries(fieldMap.mapping)
    : [];

  return (
    <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-4">
      <h3 className="text-sm font-semibold text-slate-700">Detalle de la conexión</h3>
      <dl className="mt-2 grid grid-cols-1 gap-y-1 text-xs text-slate-600 sm:grid-cols-2">
        <dt className="font-medium text-slate-500">Estado</dt>
        <dd>{cfg.state}</dd>
        <dt className="font-medium text-slate-500">Cuenta del proveedor</dt>
        <dd>{cfg.providerAccountId || "—"}</dd>
        <dt className="font-medium text-slate-500">Conectado</dt>
        <dd>{cfg.connectedAt ? new Date(cfg.connectedAt).toLocaleString() : "—"}</dd>
        <dt className="font-medium text-slate-500">Última sincronización</dt>
        <dd>{cfg.lastSyncAt ? new Date(cfg.lastSyncAt).toLocaleString() : "—"}</dd>
        <dt className="font-medium text-slate-500">Crear caso automáticamente</dt>
        <dd>{cfg.autoCreateMatter ? "Sí" : "No"}</dd>
        <dt className="font-medium text-slate-500">Redirect URI</dt>
        <dd className="truncate">{cfg.redirectUri || "—"}</dd>
      </dl>
      <h4 className="mt-4 text-sm font-semibold text-slate-700">Mapa de campos (Section → Matter)</h4>
      {error && <p className="mt-2 text-xs text-rose-600">{error}</p>}
      {!error && fieldMap === null && (
        <p className="mt-2 text-xs text-slate-400">Cargando mapa…</p>
      )}
      {!error && fieldMap !== null && mappingEntries.length === 0 && (
        <p className="mt-2 text-xs text-slate-500">Sin mapa configurado todavía.</p>
      )}
      {mappingEntries.length > 0 && (
        <table className="mt-2 w-full text-xs">
          <thead className="text-left text-slate-500">
            <tr>
              <th className="py-1 pr-4 font-medium">Campo juris</th>
              <th className="py-1 font-medium">Origen ({cfg.kind})</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 font-mono">
            {mappingEntries.map(([k, v]) => (
              <tr key={k}>
                <td className="py-1 pr-4 text-slate-700">{k}</td>
                <td className="py-1 text-slate-500">{String(v)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function IntegrationsPage() {
  const { session } = useAuth();
  const { t } = useIntl();
  const [pms, setPms] = useState<PmsConfig[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const refresh = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    if (!session) return;
    apiJSON<PmsConfig[]>("/api/v1/tenant/pms", { token: session.token })
      .then(setPms)
      .catch((e) => setError(String(e.message ?? "fetch failed")));
  }, [session, tick]);

  return (
    <div>
      <h1 className="text-2xl font-semibold text-brand-900">{t("nav.integrations")}</h1>
      {error && <p className="mt-4 text-sm text-rose-600">{error}</p>}
      <ul className="mt-6 space-y-3">
        {pms.map((p) => {
          const expanded = expandedId === p.id;
          return (
            <li key={p.id} className="rounded-md bg-white p-3 ring-1 ring-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium">{p.kind}</span>
                  {p.providerAccountId && (
                    <span className="ml-2 text-xs text-slate-500">org: {p.providerAccountId}</span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={
                      p.state === "ACTIVE"
                        ? "text-xs font-medium text-emerald-700"
                        : p.state === "NEEDS_RECONNECT"
                        ? "text-xs font-medium text-amber-700"
                        : "text-xs text-slate-500"
                    }
                  >
                    {p.state}
                  </span>
                  <button
                    type="button"
                    onClick={() => setExpandedId(expanded ? null : p.id)}
                    className="rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-100"
                  >
                    {expanded ? "Ocultar detalle" : "Ver detalle"}
                  </button>
                </div>
              </div>
              {p.lastError && <p className="mt-1 text-xs text-rose-600">{p.lastError}</p>}
              {p.kind === "FILEVINE" && p.state !== "ACTIVE" && (
                <FilevineConnectForm configId={p.id} onConnected={refresh} />
              )}
              {expanded && <IntegrationDetail cfg={p} />}
            </li>
          );
        })}
        {pms.length === 0 && !error && (
          <li className="text-slate-500">No integrations configured.</li>
        )}
      </ul>
    </div>
  );
}
